"use client";

import { c as _c } from "react/compiler-runtime";
import type { FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { cn } from "@/utilities/core/cn";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { LivescoreSport } from "@/views/livescore/utils/livescore";
import { asCompetitor, getResultScore } from "@/views/livescore/utils/livescore";
import { MatchTimeColumn } from "./MatchTimeColumn";
import { getBreakdownPeriods } from "./score-helpers";
interface PeriodsMatchRowProps {
  event: FUSportsLivescoreEvent;
  entities: Record<string, SearchEntityResult>;
  /** Owning sport — controls time-column render and live-color scheme. */
  sport: LivescoreSport;
  /** Label prefix for each period column, e.g. "Q" or "P". */
  periodLabel: string;
  /** Cap on regulation period columns. OT/penalty columns always render on top. */
  maxRegulationPeriods: number;
}

/**
 * Shared row layout for team sports that display per-period breakdowns
 * plus an aggregate total (basketball quarters, hockey periods, american
 * football quarters). Tennis is handled separately because its "total"
 * is derived from sets-won rather than an aggregate score key.
 */
export function PeriodsMatchRow(t0) {
  const $ = _c(89);
  const {
    event,
    entities,
    sport,
    periodLabel,
    maxRegulationPeriods
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] !== event.status?.type) {
    t1 = event.status?.type?.toUpperCase();
    $[0] = event.status?.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statusType = t1;
  const isLive = statusType === "LIVE";
  const isFinished = statusType === "FINISHED";
  const isActive = isLive || isFinished;
  let c2Won;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let totalScore;
  let totalScoreClass;
  if ($[2] !== entities || $[3] !== event || $[4] !== isActive || $[5] !== isFinished || $[6] !== isLive || $[7] !== labels || $[8] !== maxRegulationPeriods || $[9] !== periodLabel || $[10] !== sport) {
    const periods = getBreakdownPeriods(event.score, periodLabel, maxRegulationPeriods);
    let t11;
    if ($[23] !== event.score) {
      t11 = getResultScore(event.score);
      $[23] = event.score;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    totalScore = t11;
    let t12;
    if ($[25] !== entities || $[26] !== event.competitorOne.id) {
      t12 = asCompetitor(entities, event.competitorOne.id);
      $[25] = entities;
      $[26] = event.competitorOne.id;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    const teamOne = t12;
    let t13;
    if ($[28] !== entities || $[29] !== event.competitorTwo.id) {
      t13 = asCompetitor(entities, event.competitorTwo.id);
      $[28] = entities;
      $[29] = event.competitorTwo.id;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    const teamTwo = t13;
    const nameOne = teamOne?.name ?? event.competitorOne.id;
    const nameTwo = teamTwo?.name ?? event.competitorTwo.id;
    const numC1 = Number(totalScore.competitorOne);
    const numC2 = Number(totalScore.competitorTwo);
    const c1Won = isFinished && !isNaN(numC1) && !isNaN(numC2) && numC1 > numC2;
    c2Won = isFinished && !isNaN(numC1) && !isNaN(numC2) && numC2 > numC1;
    let t14;
    if ($[31] !== isLive) {
      t14 = won => cn("w-8 text-center text-sm font-bold tabular-nums shrink-0 border-l border-border/60", isLive ? "text-orange-400" : "text-foreground", won && "bg-foreground/5 rounded-sm");
      $[31] = isLive;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    totalScoreClass = t14;
    t9 = "flex items-stretch";
    if ($[33] !== event || $[34] !== sport) {
      t10 = <div className="w-16 shrink-0 flex items-center justify-center px-1 py-3.5"><MatchTimeColumn event={event} sport={sport} /></div>;
      $[33] = event;
      $[34] = sport;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    t6 = "flex-1 py-3 min-w-0";
    t7 = isActive && periods.length > 0 && <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-0.5 pr-1"><div className="w-8 shrink-0" /><div className="flex-1" />{periods.map(_temp)}<div className="w-8 text-center font-semibold text-foreground/60 shrink-0 border-l border-border/60">{labels.livescore.total}</div></div>;
    const t15 = teamOne?.assets?.logo;
    const t16 = teamOne?.country?.code;
    const t17 = teamOne?.country?.flag;
    const t18 = teamOne?.metadata?.branding?.primaryColor;
    const t19 = teamOne?.metadata?.branding?.secondaryColor;
    let t20;
    if ($[36] !== nameOne || $[37] !== t15 || $[38] !== t16 || $[39] !== t17 || $[40] !== t18 || $[41] !== t19) {
      t20 = <TeamLogo src={t15} alt={nameOne} size="md" countryCode={t16} countryFlag={t17} primaryColor={t18} secondaryColor={t19} />;
      $[36] = nameOne;
      $[37] = t15;
      $[38] = t16;
      $[39] = t17;
      $[40] = t18;
      $[41] = t19;
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    const t21 = c1Won ? "font-semibold" : c2Won ? "text-muted-foreground" : "font-medium";
    let t22;
    if ($[43] !== t21) {
      t22 = cn("flex-1 text-sm truncate", t21);
      $[43] = t21;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    let t23;
    if ($[45] !== nameOne || $[46] !== t22) {
      t23 = <span className={t22}>{nameOne}</span>;
      $[45] = nameOne;
      $[46] = t22;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    let t24;
    if ($[48] !== c1Won || $[49] !== isActive || $[50] !== totalScoreClass) {
      t24 = isActive ? totalScoreClass(c1Won) : "w-8 text-center text-sm text-muted-foreground shrink-0";
      $[48] = c1Won;
      $[49] = isActive;
      $[50] = totalScoreClass;
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    const t25 = isActive ? totalScore.competitorOne ?? "-" : "-";
    let t26;
    if ($[52] !== t24 || $[53] !== t25) {
      t26 = <div className={t24}>{t25}</div>;
      $[52] = t24;
      $[53] = t25;
      $[54] = t26;
    } else {
      t26 = $[54];
    }
    t8 = <div className="flex items-center gap-2 pr-1">{t20}{t23}{isActive && periods.map(_temp2)}{t26}</div>;
    t2 = "flex items-center gap-2 mt-0.5 pr-1";
    const t27 = teamTwo?.assets?.logo;
    const t28 = teamTwo?.country?.code;
    const t29 = teamTwo?.country?.flag;
    const t30 = teamTwo?.metadata?.branding?.primaryColor;
    const t31 = teamTwo?.metadata?.branding?.secondaryColor;
    if ($[55] !== nameTwo || $[56] !== t27 || $[57] !== t28 || $[58] !== t29 || $[59] !== t30 || $[60] !== t31) {
      t3 = <TeamLogo src={t27} alt={nameTwo} size="md" countryCode={t28} countryFlag={t29} primaryColor={t30} secondaryColor={t31} />;
      $[55] = nameTwo;
      $[56] = t27;
      $[57] = t28;
      $[58] = t29;
      $[59] = t30;
      $[60] = t31;
      $[61] = t3;
    } else {
      t3 = $[61];
    }
    const t32 = c2Won ? "font-semibold" : c1Won ? "text-muted-foreground" : "font-medium";
    let t33;
    if ($[62] !== t32) {
      t33 = cn("flex-1 text-sm truncate", t32);
      $[62] = t32;
      $[63] = t33;
    } else {
      t33 = $[63];
    }
    if ($[64] !== nameTwo || $[65] !== t33) {
      t4 = <span className={t33}>{nameTwo}</span>;
      $[64] = nameTwo;
      $[65] = t33;
      $[66] = t4;
    } else {
      t4 = $[66];
    }
    t5 = isActive && periods.map(_temp3);
    $[2] = entities;
    $[3] = event;
    $[4] = isActive;
    $[5] = isFinished;
    $[6] = isLive;
    $[7] = labels;
    $[8] = maxRegulationPeriods;
    $[9] = periodLabel;
    $[10] = sport;
    $[11] = c2Won;
    $[12] = t10;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = totalScore;
    $[22] = totalScoreClass;
  } else {
    c2Won = $[11];
    t10 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
    totalScore = $[21];
    totalScoreClass = $[22];
  }
  let t11;
  if ($[67] !== c2Won || $[68] !== isActive || $[69] !== totalScoreClass) {
    t11 = isActive ? totalScoreClass(c2Won) : "w-8 text-center text-sm text-muted-foreground shrink-0";
    $[67] = c2Won;
    $[68] = isActive;
    $[69] = totalScoreClass;
    $[70] = t11;
  } else {
    t11 = $[70];
  }
  const t12 = isActive ? totalScore.competitorTwo ?? "-" : "-";
  let t13;
  if ($[71] !== t11 || $[72] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== t13 || $[75] !== t2 || $[76] !== t3 || $[77] !== t4 || $[78] !== t5) {
    t14 = <div className={t2}>{t3}{t4}{t5}{t13}</div>;
    $[74] = t13;
    $[75] = t2;
    $[76] = t3;
    $[77] = t4;
    $[78] = t5;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] !== t14 || $[81] !== t6 || $[82] !== t7 || $[83] !== t8) {
    t15 = <div className={t6}>{t7}{t8}{t14}</div>;
    $[80] = t14;
    $[81] = t6;
    $[82] = t7;
    $[83] = t8;
    $[84] = t15;
  } else {
    t15 = $[84];
  }
  let t16;
  if ($[85] !== t10 || $[86] !== t15 || $[87] !== t9) {
    t16 = <div className={t9}>{t10}{t15}</div>;
    $[85] = t10;
    $[86] = t15;
    $[87] = t9;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  return t16;
}
function _temp3(p_1) {
  return <div key={p_1.key} className="w-8 text-center text-xs text-muted-foreground tabular-nums shrink-0">{p_1.c2 ?? ""}</div>;
}
function _temp2(p_0) {
  return <div key={p_0.key} className="w-8 text-center text-xs text-muted-foreground tabular-nums shrink-0">{p_0.c1 ?? ""}</div>;
}
function _temp(p) {
  return <div key={p.key} className="w-8 text-center shrink-0">{p.label}</div>;
}